import React from 'react';
import { Card, Row, Col, Typography, Space, Tag, Button, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  DollarOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import {
  AI_FEATURE_ENGINE,
  getFeatureDisplayName,
  getFeatureEngine,
  getFeatureEngineLabel,
  getFeatureShortLabel,
} from '../../utils/aiFeatureMetadata';

const { Title, Text } = Typography;

export const DashboardCards = ({
  dashboardData,
  showGa4Details,
  setShowGa4Details,
  icons,
  aiUsageData,
  showAiOverallDetails,
  setShowAiOverallDetails,
  showAiProviderDetails,
  setShowAiProviderDetails,
  showAiModelDetails,
  setShowAiModelDetails,
  showAiFeatureDetails,
  setShowAiFeatureDetails,
  showAiInternalDetails,
  setShowAiInternalDetails,
  showAiCustomerDetails,
  setShowAiCustomerDetails,
}) => {
  const totalUsersIcon = icons.totalUsersIcon;
  const activeUsersIcon = icons.activeUsersIcon;
  const activeUsers14DaysIcon = icons.activeUsers14DaysIcon;
  const inactiveUsersIcon = icons.inactiveUsersIcon;
  const newSignupsIcon = icons.newSignupsIcon;
  const propertiesIcon = icons.propertiesIcon;
  const connectionsIcon = icons.connectionsIcon;
  const ga4SyncIcon = icons.ga4SyncIcon;
  const aiOverallIcon = icons.aiOverallIcon;
  const aiProviderIcon = icons.aiProviderIcon;
  const aiModelIcon = icons.aiModelIcon;
  const aiFeatureIcon = icons.aiFeatureIcon;

  const getGa4StatusTag = (key, value) => {
    const config = {
      total_connections: { color: 'blue', icon: <DatabaseOutlined />, label: 'TOTAL' },
      healthy: { color: 'success', icon: <CheckCircleOutlined />, label: 'HEALTHY' },
      issues: { color: 'error', icon: <CloseCircleOutlined />, label: 'ISSUES' },
      expired: { color: 'warning', icon: <ClockCircleOutlined />, label: 'EXPIRED' },
      disabled: { color: 'default', icon: <CloseCircleOutlined />, label: 'DISABLED' },
      inactive: { color: 'processing', icon: <SyncOutlined />, label: 'INACTIVE' }
    };
    
    const itemConfig = config[key] || { color: 'default', icon: null, label: key.toUpperCase() };
    
    return (
      <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
        <Space size={4}>
          {itemConfig.icon}
          <Text style={{ fontSize: '12px' }}>{itemConfig.label}</Text>
        </Space>
        <Text strong style={{ fontSize: '12px' }}>{value}</Text>
      </div>
    );
  };

  const formatCost = (cost) => {
    if (!cost || cost === 0) return '$0.00';
    if (cost < 0.0001) return `$${cost.toExponential(2)}`;
    if (cost < 0.01) return `$${cost.toFixed(6)}`;
    return `$${cost.toFixed(4)}`;
  };

  const formatNumber = (num) => (num != null ? num.toLocaleString() : '0');

  const getFeatureTagColor = (engine) =>
    engine === AI_FEATURE_ENGINE.POLARS ? 'green' : 'purple';

  const buildFeatureTooltip = (featureKey, stats) => {
    const engine = getFeatureEngine(featureKey);
    const lines = [
      getFeatureDisplayName(featureKey),
      getFeatureEngineLabel(engine),
    ];
    if (stats) {
      lines.push(`${formatNumber(stats.total_requests)} reqs · ${formatCost(stats.total_cost)}`);
    }
    return lines.join('\n');
  };

  const renderFeatureTag = (featureKey, stats = null) => {
    const engine = getFeatureEngine(featureKey);
    return (
      <Tooltip title={buildFeatureTooltip(featureKey, stats)}>
        <Tag
          color={getFeatureTagColor(engine)}
          style={{ margin: 0, maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}
        >
          {getFeatureShortLabel(featureKey)}
        </Tag>
      </Tooltip>
    );
  };

  const partitionFeaturesByEngine = (features) => {
    const polars = [];
    const paid = [];
    features.forEach((f) => {
      if (getFeatureEngine(f.feature) === AI_FEATURE_ENGINE.POLARS) {
        polars.push(f);
      } else {
        paid.push(f);
      }
    });
    return { polars, paid };
  };

  const renderFeatureDetailRow = (featureKey, stats) => {
    const engine = getFeatureEngine(featureKey);
    return (
      <div
        key={featureKey}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}
      >
        <Tooltip title={getFeatureDisplayName(featureKey)}>
          <Space size={4} style={{ flex: 1, minWidth: 0, maxWidth: '55%' }}>
            <Tag color={getFeatureTagColor(engine)} style={{ margin: 0, fontSize: '9px', lineHeight: '16px', padding: '0 4px' }}>
              {engine === AI_FEATURE_ENGINE.POLARS ? 'Polars' : 'LLM'}
            </Tag>
            <Text style={{ fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'help' }}>
              {getFeatureShortLabel(featureKey)}
            </Text>
          </Space>
        </Tooltip>
        <Space size={4}>
          <Tag color="green" style={{ margin: 0, fontSize: '10px' }}>{formatCost(stats.total_cost)}</Tag>
          <Tag color="blue" style={{ margin: 0, fontSize: '10px' }}>{formatNumber(stats.total_requests)} reqs</Tag>
        </Space>
      </div>
    );
  };

  const renderSummaryRow = (label, value, suffix = null) => (
    <div key={String(label)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px', gap: '8px' }}>
      <Text style={{ fontSize: '11px', flex: 1, minWidth: 0 }}>{label}</Text>
      <Space size={6}>
        <Text strong style={{ fontSize: '11px' }}>{value}</Text>
        {suffix}
      </Space>
    </div>
  );

  const internalSummary = aiUsageData?.internal_summary;
  const customerSummary = aiUsageData?.customer_summary;

  const renderFeatureSection = (title, features) => {
    if (!features.length) return null;
    return (
      <>
        <Text type="secondary" style={{ fontSize: '10px', display: 'block', marginBottom: '4px' }}>
          {title}
        </Text>
        {features.map((f) => renderFeatureDetailRow(f.feature, f))}
      </>
    );
  };

  const renderDetailRow = (label, value, extra = null) => (
    <div
      key={label}
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}
    >
      <Text style={{ fontSize: '11px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%' }}>
        {label}
      </Text>
      <Space size={4}>
        {extra}
        <Tag color="blue" style={{ margin: 0, fontSize: '10px' }}>{value} reqs</Tag>
      </Space>
    </div>
  );

  // Updated count array with all data from API
  const count = dashboardData ? [
    {
      today: "Total Users",
      title: dashboardData.total_users?.toString() || "0",
      persent: "+5%",
      icon: totalUsersIcon,
      bnb: "bnb2",
      description: "Total registered users in the system"
    },
    {
      today: "Active Users (7 days)",
      title: dashboardData.active_users_last_7_days?.toString() || "0",
      persent: "+20%",
      icon: activeUsersIcon,
      bnb: "bnb2",
      description: "Users active in the last 7 days"
    },
    {
      today: "Active Users (14 days)",
      title: dashboardData.active_users_last_14_days?.toString() || "0",
      persent: "+15%",
      icon: activeUsers14DaysIcon,
      bnb: "bnb2",
      description: "Users active in the last 14 days"
    },
    {
      today: "Inactive Users (14 days)",
      title: dashboardData.inactive_users_last_14_days?.toString() || "0",
      persent: "-10%",
      icon: inactiveUsersIcon,
      bnb: "redtext",
      description: "Users not active in the last 14 days"
    },
    {
      today: "New Signups Today",
      title: dashboardData.new_signups_today?.toString() || "0",
      persent: dashboardData.new_signups_today > 0 ? "+" + dashboardData.new_signups_today + "%" : "0%",
      icon: newSignupsIcon,
      bnb: dashboardData.new_signups_today > 0 ? "bnb2" : "graytext",
      description: "New user registrations today"
    },
    {
      today: "Connected Properties",
      title: dashboardData.total_connected_properties?.toString() || "0",
      persent: "+12%",
      icon: propertiesIcon,
      bnb: "bnb2",
      description: "Total properties connected to the system"
    },
    {
      today: "Total Connections",
      title: dashboardData.total_connections?.toString() || "0",
      persent: "+8%",
      icon: connectionsIcon,
      bnb: "bnb2",
      description: "Total connections established"
    },
  ] : [];

  // GA4 Sync Summary Card Data
  const ga4SyncSummary = dashboardData?.ga4_sync_summary ? {
    today: "GA4 Sync Status",
    title: dashboardData.ga4_sync_summary.total_connections?.toString() || "0",
    persent: "",
    icon: ga4SyncIcon,
    bnb: "bnb2",
    description: "Google Analytics 4 connections",
    summaryData: dashboardData.ga4_sync_summary
  } : null;

  return (
    <Row className="rowgap-vbox" gutter={[24, 16]}>
      {count.map((c, index) => (
        <Col
          key={index}
          xs={24}
          sm={24}
          md={12}
          lg={8}
          xl={6}
          className="mb-24"
        >
          <Card
            bordered={false}
            className="criclebox"
            hoverable
            style={{ height: '100%' }}
          >
            <div className="number">
              <Row align="middle" gutter={[24, 0]}>
                <Col xs={18}>
                  <span className="card-label">{c.today}</span>
                  <Title level={3}>
                    {c.title} <small className={c.bnb}>{c.persent}</small>
                  </Title>
                  {c.description && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {c.description}
                    </Text>
                  )}
                </Col>
                <Col xs={6}>
                  <div className="icon-box" style={{
                    backgroundColor: c.bnb === 'redtext' ? '#ff4d4f' : c.bnb === 'graytext' ? '#8c8c8c' : '#52c41a',
                    borderRadius: '12px',
                    padding: '12px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {c.icon}
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>
      ))}
      
      {/* GA4 Sync Summary Card */}
      {ga4SyncSummary && (
        <Col xs={24} sm={24} md={12} lg={8} xl={6} className="mb-24">
          <Card
            bordered={false}
            className="criclebox"
            hoverable
            style={{ height: '100%', minHeight: '160px' }}
          >
            <div className="number">
              <Row align="middle" gutter={[24, 0]}>
                <Col xs={18}>
                  <span className="card-label">{ga4SyncSummary.today}</span>
                  <Title level={3}>
                    {ga4SyncSummary.title}
                  </Title>
                  {ga4SyncSummary.description && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {ga4SyncSummary.description}
                    </Text>
                  )}
                  
                  {/* Compact Status Indicators */}
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      <Col flex="none">
                        <Tooltip title="Healthy Connections">
                          <Tag 
                            color="success" 
                            icon={<CheckCircleOutlined />}
                            style={{ margin: 0, cursor: 'pointer' }}
                          >
                            {ga4SyncSummary.summaryData?.healthy || 0}
                          </Tag>
                        </Tooltip>
                      </Col>
                      <Col flex="none">
                        <Tooltip title="Issues">
                          <Tag 
                            color={ga4SyncSummary.summaryData?.issues > 0 ? "error" : "default"} 
                            icon={<CloseCircleOutlined />}
                            style={{ margin: 0, cursor: 'pointer' }}
                          >
                            {ga4SyncSummary.summaryData?.issues || 0}
                          </Tag>
                        </Tooltip>
                      </Col>
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button 
                          type="link" 
                          size="small" 
                          onClick={() => setShowGa4Details(!showGa4Details)}
                          icon={showGa4Details ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ 
                            padding: '0', 
                            fontSize: '12px', 
                            height: 'auto',
                            color: '#1890ff'
                          }}
                        >
                          {showGa4Details ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  
                  {/* Detailed View - Collapsible */}
                  {showGa4Details && ga4SyncSummary.summaryData && (
                    <div style={{ 
                      marginTop: '12px', 
                      padding: '8px', 
                      backgroundColor: '#fafafa', 
                      borderRadius: '6px',
                      border: '1px solid #f0f0f0'
                    }}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        {Object.entries(ga4SyncSummary.summaryData).map(([key, value]) => (
                          getGa4StatusTag(key, value)
                        ))}
                      </Space>
                    </div>
                  )}
                </Col>
                <Col xs={6}>
                  <div className="icon-box" style={{
                    backgroundColor: '#1890ff',
                    borderRadius: '12px',
                    padding: '12px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    {ga4SyncSummary.icon}
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>
      )}

      {/* AI Usage Overview Card */}
      {aiUsageData?.overall && (
        <Col xs={24} sm={24} md={12} lg={8} xl={6} className="mb-24">
          <Card bordered={false} className="criclebox" hoverable style={{ height: '100%', minHeight: '160px' }}>
            <div className="number">
              <Row align="middle" gutter={[24, 0]}>
                <Col xs={18}>
                  <span className="card-label">AI Usage Overview</span>
                  <Title level={3}>{formatNumber(aiUsageData.overall.total_requests)}</Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Total AI API requests</Text>
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      <Col flex="none">
                        <Tooltip title="Total Cost"><Tag color="purple" icon={<DollarOutlined />} style={{ margin: 0 }}>{formatCost(aiUsageData.overall.total_cost)}</Tag></Tooltip>
                      </Col>
                      <Col flex="none">
                        <Tooltip title="Avg Tokens/Request"><Tag color="cyan" icon={<ThunderboltOutlined />} style={{ margin: 0 }}>{Math.round(aiUsageData.overall.avg_tokens_per_request)}</Tag></Tooltip>
                      </Col>
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button type="link" size="small" onClick={() => setShowAiOverallDetails(!showAiOverallDetails)}
                          icon={showAiOverallDetails ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ padding: '0', fontSize: '12px', height: 'auto', color: '#722ed1' }}>
                          {showAiOverallDetails ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {showAiOverallDetails && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        {[
                          ['Total Cost', formatCost(aiUsageData.overall.total_cost)],
                          ['Total Tokens', formatNumber(aiUsageData.overall.total_tokens)],
                          ['Prompt Tokens', formatNumber(aiUsageData.overall.total_prompt_tokens)],
                          ['Completion Tokens', formatNumber(aiUsageData.overall.total_completion_tokens)],
                          ['Total Requests', formatNumber(aiUsageData.overall.total_requests)],
                          ['Avg Cost / Request', formatCost(aiUsageData.overall.avg_cost_per_request)],
                          ['Avg Tokens / Request', Math.round(aiUsageData.overall.avg_tokens_per_request).toLocaleString()],
                          ...(aiUsageData.by_usage_class || []).map((row) => [
                            `COGS — ${row.usage_class}`,
                            `${formatCost(row.total_cost)} (${formatNumber(row.total_requests)} req)`,
                          ]),
                        ].map(([k, v]) => (
                          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <Text style={{ fontSize: '11px' }}>{k}</Text>
                            <Text strong style={{ fontSize: '11px' }}>{v}</Text>
                          </div>
                        ))}
                      </Space>
                    </div>
                  )}
                </Col>
                <Col xs={6}>
                  <div className="icon-box" style={{ backgroundColor: '#722ed1', borderRadius: '12px', padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {aiOverallIcon}
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>
      )}

      {/* AI by Provider Card */}
      {aiUsageData?.by_provider?.length > 0 && (
        <Col xs={24} sm={24} md={12} lg={8} xl={6} className="mb-24">
          <Card bordered={false} className="criclebox" hoverable style={{ height: '100%', minHeight: '160px' }}>
            <div className="number">
              <Row align="middle" gutter={[24, 0]}>
                <Col xs={18}>
                  <span className="card-label">AI by Provider</span>
                  <Title level={3}>{aiUsageData.by_provider.length}</Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>Active AI providers</Text>
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      {aiUsageData.by_provider.slice(0, 2).map((p) => (
                        <Col flex="none" key={p.provider}>
                          <Tooltip title={`${formatNumber(p.total_requests)} reqs · ${formatCost(p.total_cost)}`}>
                            <Tag color="blue" style={{ margin: 0, textTransform: 'capitalize' }}>{p.provider}</Tag>
                          </Tooltip>
                        </Col>
                      ))}
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button type="link" size="small" onClick={() => setShowAiProviderDetails(!showAiProviderDetails)}
                          icon={showAiProviderDetails ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ padding: '0', fontSize: '12px', height: 'auto', color: '#1890ff' }}>
                          {showAiProviderDetails ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {showAiProviderDetails && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        {aiUsageData.by_provider.map((p) => renderDetailRow(
                          <span style={{ textTransform: 'capitalize' }}>{p.provider}</span>,
                          formatNumber(p.total_requests),
                          <Tag color="green" style={{ margin: 0, fontSize: '10px' }}>{formatCost(p.total_cost)}</Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                </Col>
                <Col xs={6}>
                  <div className="icon-box" style={{ backgroundColor: '#1890ff', borderRadius: '12px', padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {aiProviderIcon}
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>
      )}

      {internalSummary && (
        <Col xs={24} sm={24} md={12} lg={8} xl={6} className="mb-24">
          <Card bordered={false} className="criclebox" hoverable style={{ height: '100%', minHeight: '160px' }}>
            <div className="number">
              <Row align="middle" gutter={[24, 0]}>
                <Col xs={18}>
                  <span className="card-label">Internal AI Cost</span>
                  <Title level={3}>{formatCost(internalSummary.total_cost)}</Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatNumber(internalSummary.total_requests)} admin/staff requests
                  </Text>
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      <Col flex="none">
                        <Tooltip title="Internal token consumption">
                          <Tag color="purple" icon={<ThunderboltOutlined />} style={{ margin: 0 }}>
                            {formatNumber(internalSummary.total_tokens)}
                          </Tag>
                        </Tooltip>
                      </Col>
                      <Col flex="none">
                        <Tooltip title="Share of total AI spend">
                          <Tag color="magenta" icon={<DollarOutlined />} style={{ margin: 0 }}>
                            {`${((internalSummary.cost_share || 0) * 100).toFixed(1)}%`}
                          </Tag>
                        </Tooltip>
                      </Col>
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setShowAiInternalDetails(!showAiInternalDetails)}
                          icon={showAiInternalDetails ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ padding: '0', fontSize: '12px', height: 'auto', color: '#c41d7f' }}
                        >
                          {showAiInternalDetails ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {showAiInternalDetails && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        {renderSummaryRow('Internal cost', formatCost(internalSummary.total_cost))}
                        {renderSummaryRow('Internal requests', formatNumber(internalSummary.total_requests))}
                        {renderSummaryRow('Internal tokens', formatNumber(internalSummary.total_tokens))}
                        {renderSummaryRow('Top internal users', formatNumber(aiUsageData?.top_internal_users?.length || 0))}
                        {(aiUsageData?.top_internal_users || []).map((user) => renderSummaryRow(
                          user.user_email,
                          formatNumber(user.total_requests),
                          <Tag color="magenta" style={{ margin: 0, fontSize: '10px' }}>{formatCost(user.total_cost)}</Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                </Col>
                <Col xs={6}>
                  <div className="icon-box" style={{ backgroundColor: '#c41d7f', borderRadius: '12px', padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {aiOverallIcon}
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>
      )}

      {customerSummary && (
        <Col xs={24} sm={24} md={12} lg={8} xl={6} className="mb-24">
          <Card bordered={false} className="criclebox" hoverable style={{ height: '100%', minHeight: '160px' }}>
            <div className="number">
              <Row align="middle" gutter={[24, 0]}>
                <Col xs={18}>
                  <span className="card-label">Customer AI Usage</span>
                  <Title level={3}>{formatNumber(customerSummary.total_requests)}</Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatCost(customerSummary.total_cost)} customer spend
                  </Text>
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      <Col flex="none">
                        <Tooltip title="Customer token consumption">
                          <Tag color="blue" icon={<ThunderboltOutlined />} style={{ margin: 0 }}>
                            {formatNumber(customerSummary.total_tokens)}
                          </Tag>
                        </Tooltip>
                      </Col>
                      <Col flex="none">
                        <Tooltip title="Share of total AI spend">
                          <Tag color="green" icon={<DollarOutlined />} style={{ margin: 0 }}>
                            {`${((customerSummary.cost_share || 0) * 100).toFixed(1)}%`}
                          </Tag>
                        </Tooltip>
                      </Col>
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => setShowAiCustomerDetails(!showAiCustomerDetails)}
                          icon={showAiCustomerDetails ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ padding: '0', fontSize: '12px', height: 'auto', color: '#1677ff' }}
                        >
                          {showAiCustomerDetails ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {showAiCustomerDetails && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        {renderSummaryRow('Customer cost', formatCost(customerSummary.total_cost))}
                        {renderSummaryRow('Customer requests', formatNumber(customerSummary.total_requests))}
                        {renderSummaryRow('Customer tokens', formatNumber(customerSummary.total_tokens))}
                        {renderSummaryRow('Top customer users', formatNumber(aiUsageData?.top_customer_users?.length || 0))}
                        {(aiUsageData?.top_customer_users || []).slice(0, 3).map((user) => renderSummaryRow(
                          user.user_email,
                          formatNumber(user.total_requests),
                          <Tag color="green" style={{ margin: 0, fontSize: '10px' }}>{formatCost(user.total_cost)}</Tag>
                        ))}
                        {(aiUsageData?.customer_feature_highlights || []).slice(0, 3).map((feature) => renderSummaryRow(
                          getFeatureDisplayName(feature.feature_name),
                          formatNumber(feature.total_requests),
                          <Tag color="blue" style={{ margin: 0, fontSize: '10px' }}>{formatCost(feature.total_cost)}</Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                </Col>
                <Col xs={6}>
                  <div className="icon-box" style={{ backgroundColor: '#1677ff', borderRadius: '12px', padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {aiProviderIcon}
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>
      )}

      {/* AI by Model Card */}
      {aiUsageData?.by_model?.length > 0 && (
        <Col xs={24} sm={24} md={12} lg={8} xl={6} className="mb-24">
          <Card bordered={false} className="criclebox" hoverable style={{ height: '100%', minHeight: '160px' }}>
            <div className="number">
              <Row align="middle" gutter={[24, 0]}>
                <Col xs={18}>
                  <span className="card-label">AI by Model</span>
                  <Title level={3}>{aiUsageData.by_model.length}</Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>AI models in use</Text>
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      {aiUsageData.by_model.slice(0, 2).map((m) => (
                        <Col flex="none" key={`${m.provider}-${m.model}`}>
                          <Tooltip title={`${m.provider} · ${formatNumber(m.total_requests)} reqs · ${formatCost(m.total_cost)}`}>
                            <Tag color="cyan" style={{ margin: 0, maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.model}</Tag>
                          </Tooltip>
                        </Col>
                      ))}
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button type="link" size="small" onClick={() => setShowAiModelDetails(!showAiModelDetails)}
                          icon={showAiModelDetails ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ padding: '0', fontSize: '12px', height: 'auto', color: '#13c2c2' }}>
                          {showAiModelDetails ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {showAiModelDetails && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Space direction="vertical" size={2} style={{ width: '100%' }}>
                        {aiUsageData.by_model.map((m) => renderDetailRow(
                          `${m.model}`,
                          formatNumber(m.total_requests),
                          <Tag color="green" style={{ margin: 0, fontSize: '10px' }}>{formatCost(m.total_cost)}</Tag>
                        ))}
                      </Space>
                    </div>
                  )}
                </Col>
                <Col xs={6}>
                  <div className="icon-box" style={{ backgroundColor: '#13c2c2', borderRadius: '12px', padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {aiModelIcon}
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>
      )}

      {/* AI by Feature Card */}
      {aiUsageData?.by_feature?.length > 0 && (() => {
        const { polars: polarsFeatures, paid: paidFeatures } = partitionFeaturesByEngine(aiUsageData.by_feature);
        return (
        <Col xs={24} sm={24} md={12} lg={8} xl={6} className="mb-24">
          <Card bordered={false} className="criclebox" hoverable style={{ height: '100%', minHeight: '160px' }}>
            <div className="number">
              <Row align="middle" gutter={[24, 0]}>
                <Col xs={18}>
                  <span className="card-label">AI by Feature</span>
                  <Title level={3}>{aiUsageData.by_feature.length}</Title>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Polars (free) &amp; paid LLM features
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Space size={4} wrap>
                      <Tooltip title="Statistical summaries via Python Polars — no LLM cost">
                        <Tag color="green" style={{ margin: 0, fontSize: '10px' }}>Polars ({polarsFeatures.length})</Tag>
                      </Tooltip>
                      <Tooltip title="OpenAI, Gemini, OpenRouter, and other paid providers">
                        <Tag color="purple" style={{ margin: 0, fontSize: '10px' }}>Paid LLM ({paidFeatures.length})</Tag>
                      </Tooltip>
                    </Space>
                  </div>
                  <div style={{ marginTop: '12px' }}>
                    <Row gutter={[8, 8]} align="middle">
                      {aiUsageData.by_feature.slice(0, 2).map((f) => (
                        <Col flex="none" key={f.feature}>
                          {renderFeatureTag(f.feature, f)}
                        </Col>
                      ))}
                      <Col flex="auto" style={{ textAlign: 'right' }}>
                        <Button type="link" size="small" onClick={() => setShowAiFeatureDetails(!showAiFeatureDetails)}
                          icon={showAiFeatureDetails ? <CloseCircleOutlined /> : <InfoCircleOutlined />}
                          style={{ padding: '0', fontSize: '12px', height: 'auto', color: '#fa8c16' }}>
                          {showAiFeatureDetails ? 'Less' : 'More'}
                        </Button>
                      </Col>
                    </Row>
                  </div>
                  {showAiFeatureDetails && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #f0f0f0' }}>
                      <Space direction="vertical" size={6} style={{ width: '100%' }}>
                        {renderFeatureSection('Polars (free)', polarsFeatures)}
                        {renderFeatureSection('Paid LLM providers', paidFeatures)}
                      </Space>
                    </div>
                  )}
                </Col>
                <Col xs={6}>
                  <div className="icon-box" style={{ backgroundColor: '#fa8c16', borderRadius: '12px', padding: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    {aiFeatureIcon}
                  </div>
                </Col>
              </Row>
            </div>
          </Card>
        </Col>
        );
      })()}
    </Row>
  );
};